import React from "react"
import { useStaticQuery, graphql, Link } from "gatsby"
import { Container } from "reactstrap"
import useIsInViewport from "use-is-in-viewport"
import styled from "styled-components"
import { FaFacebook } from "react-icons/fa"
import { FaPinterest } from "react-icons/fa"
import vars from "styles/variables"
import { convertToInternalUrl } from "utils/globalUtil"
import ContactBar from "components/ui/ContactBar"
import Image from "components/common/Image"
import MainLogo from "components/svg/MainLogo"

function Footer({ pageId, isHomePage }) {
  const [isInViewport, targetRef] = useIsInViewport()
  const data = useStaticQuery(graphql`
    query FooterQuery {
      menu: allWpMenuItem(
        filter: { menu: { node: { slug: { eq: "footer" } } } }
        sort: { order: ASC }
      ) {
        items: edges {
          node {
            label
            url
            slug: uri
          }
        }
      }
      wp {
        inspiredGlobalOptions {
          globalOptions {
            inspiredOptionsContactsPhone
            inspiredOptionsSocialFacebook
            inspiredOptionsContactsAddress
          }
          additionalOptionsGroup {
            inspiredOptionsSocialPinterest
            footerText
            companyName
            footerImages {
              footerImage {
                altText
                sourceUrl
                width
                height
              }
            }
            showrooms {
              title
              telephoneNumber
              address
            }
          }
        }
      }
      site {
        siteMetadata {
          title
        }
      }
    }
  `)

  const {
    inspiredOptionsContactsPhone: tel,
    inspiredOptionsSocialFacebook: facebook,
    inspiredOptionsContactsAddress: address,
  } = data.wp.inspiredGlobalOptions.globalOptions || {}

  const {
    inspiredOptionsSocialPinterest: pinterest,
    footerText: footerText,
    companyName: companyName,
    footerImages: footerImages = [],
    showrooms: showrooms = [],
  } = data.wp.inspiredGlobalOptions.additionalOptionsGroup || {}

  const { title } = data.site.siteMetadata || {}
  const { items: footerNav } = data.menu || []

  const currentDate = new Date()
  const legalCompanyName = companyName || title
  const socialLinks = [
    {
      href: facebook,
      label: "Facebook",
      Icon: FaFacebook,
    },
    {
      href: pinterest,
      label: "Pinterest",
      Icon: FaPinterest,
    },
  ].filter(item => item.href)

  return (
    <StyledFooterContainer>
      {/* update to 3691 */}
      {pageId !== 4010 && (
        <ContactBar
          isNotFixed={isInViewport}
          pageId={pageId}
          isHomePage={isHomePage}
        />
      )}
      <StyledFooter ref={targetRef}>
        <Container>
          <StyledFooterGrid>
            <StyledShowroomsColumn>
              <MainLogo className="footer-logo" />
              <div itemScope itemType="http://schema.org/ProfessionalService">
                {showrooms.map((showroom, idx) => (
                  <div
                    key={idx}
                    itemProp="address"
                    itemType="http://schema.org/PostalAddress"
                    className="showroom"
                  >
                    <div className="showroom-title">{showroom.title}</div>
                    <div
                      className="showroom-address"
                      dangerouslySetInnerHTML={{ __html: showroom.address }}
                    />
                    <div className="showroom-tel">
                      Tel:{" "}
                      <a href={`tel:${showroom.telephoneNumber}`}>
                        <span itemProp="telephone">
                          {showroom.telephoneNumber}
                        </span>
                      </a>
                    </div>
                  </div>
                ))}
                {showrooms.length === 0 && (
                  <div
                    itemProp="address"
                    itemType="http://schema.org/PostalAddress"
                    className="showroom"
                  >
                    <div
                      className="showroom-address"
                      dangerouslySetInnerHTML={{ __html: address }}
                    />
                    <div className="showroom-tel">
                      Tel:{" "}
                      <a href={`tel:${tel}`}>
                        <span itemProp="telephone">{tel}</span>
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </StyledShowroomsColumn>

            <StyledFooterNav aria-label="Footer">
              {footerNav.map(item => (
                <Link
                  key={item.node.slug}
                  to={convertToInternalUrl(item.node.url)}
                >
                  {item.node.label}
                </Link>
              ))}
            </StyledFooterNav>

            <div>
              {socialLinks.length > 0 && (
                <StyledSocial>
                  <div className="footer-social-title">follow us</div>
                  <div className="footer-social-icons">
                    {socialLinks.map(({ href, label, Icon }) => (
                      <a
                        key={label}
                        href={href}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={label}
                      >
                        <Icon />
                      </a>
                    ))}
                  </div>
                </StyledSocial>
              )}

              <StyledLegal>
                {legalCompanyName && (
                  <div>
                    &copy;{currentDate.getFullYear()} {legalCompanyName}
                  </div>
                )}
                {footerText && <div>{footerText}</div>}
              </StyledLegal>

              {footerImages.length > 0 && (
                <StyledAccreditations>
                  {footerImages.map((item, index) => (
                    <div key={index}>
                      <Image
                        src={item.footerImage?.sourceUrl}
                        alt={item.footerImage?.altText || ""}
                        width={item.footerImage?.width || ""}
                        height={item.footerImage?.height || ""}
                      />
                    </div>
                  ))}
                </StyledAccreditations>
              )}
            </div>
          </StyledFooterGrid>
        </Container>
      </StyledFooter>
    </StyledFooterContainer>
  )
}

export default Footer

const StyledFooterContainer = styled.div`
  background: ${vars.primary};
  position: relative;
  padding-top: 106px;

  @media (max-width: ${vars.screen_sm_max}) {
    padding-top: 100px;
  }
`

const StyledFooter = styled.footer`
  color: ${vars.white};
  padding: 3.5rem 0 3.25rem;
  font-size: 0.95rem;
  line-height: 1.55;

  @media (max-width: ${vars.screen_sm_max}) {
    padding: 2.5rem 0;
  }

  a {
    color: ${vars.white};
    transition: ease-in-out 200ms;

    &:hover {
      color: ${vars.secondary};
    }
  }
`

const StyledFooterGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(280px, 1.25fr) minmax(160px, 0.7fr) minmax(
      280px,
      1fr
    );
  gap: 4.75rem;
  align-items: start;

  @media (max-width: ${vars.screen_md_max}) {
    grid-template-columns: 1.25fr 1fr;
    gap: 3rem;
  }

  @media (max-width: ${vars.screen_sm_max}) {
    grid-template-columns: 1fr;
    gap: 2.5rem;
  }
`

const StyledShowroomsColumn = styled.div`
  .footer-logo {
    display: block;
    width: 148px;
    height: auto;
    margin-bottom: 2rem;
  }

  .showroom {
    margin-bottom: 1.75rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .showroom-title {
    font-weight: 700;
    margin-bottom: 0.55rem;
  }

  .showroom-address {
    p {
      margin-bottom: 0.45rem;
      line-height: 1.6;
    }
  }

  .showroom-tel {
    margin-top: 0.9rem;

    a {
      text-decoration: underline;
      text-underline-offset: 2px;
    }
  }
`

const StyledFooterNav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;

  @media (max-width: ${vars.screen_sm_max}) {
    gap: 0.7rem;
  }
`

const StyledSocial = styled.div`
  .footer-social-title {
    margin-bottom: 0.7rem;
    text-transform: lowercase;
  }

  .footer-social-icons {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    font-size: 1.75rem;
    line-height: 1;

    svg {
      transition: ease-in-out 200ms;
    }

    a:hover svg {
      transform: scale(1.12);
    }
  }
`

const StyledLegal = styled.div`
  margin-top: 2rem;
  line-height: 1.45;
  text-transform: lowercase;
`

const StyledAccreditations = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 2.25rem;

  img {
    display: block;
    width: auto;
    max-width: 82px;
    max-height: 31px;
    object-fit: contain;
  }

  @media (max-width: ${vars.screen_sm_max}) {
    flex-wrap: wrap;
  }
`

